import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, ExternalLink } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useAdminData } from "@/hooks/useAdminData";
import AdminStatsBar from "@/components/admin/AdminStatsBar";
import AdminDataTable from "@/components/admin/AdminDataTable";

const TABS = [
  { id: "applications", label: "Applications" },
  { id: "partners", label: "Partner Inquiries" },
  { id: "contacts", label: "Contact Inquiries" },
  { id: "programs", label: "Programs" },
  { id: "packages", label: "Packages" },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("applications");
  const { stats, items, loading, deleteItem, updateApplicationStatus } = useAdminData(tab);

  const handleLogout = async () => {
    await logout();
    nav("/admin/login");
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await deleteItem(tab, id);
      toast.success("Deleted");
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.error("Delete failed:", err);
      }
      toast.error("Delete failed");
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await updateApplicationStatus(id, status);
      toast.success("Status updated");
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.error("Status update failed:", err);
      }
      toast.error("Status update failed");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <header className="bg-white border-b border-slate-100 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
              data-testid="admin-back-to-site"
            >
              <ExternalLink className="w-3.5 h-3.5" /> View site
            </Link>
            <span className="text-slate-300">/</span>
            <span className="font-display font-bold text-slate-900">Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-xs text-slate-500">{user?.email}</span>
            <button
              onClick={handleLogout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 lg:px-8 py-8">
        <AdminStatsBar stats={stats} />

        <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="admin-tabs">
          {TABS.map((t) => {
            const isActive = tab === t.id;
            const btnClass = isActive
              ? "bg-slate-900 text-white"
              : "bg-white border border-slate-200 text-slate-700 hover:border-slate-300";
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`admin-tab-${t.id}`}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${btnClass}`}
              >
                {t.label}
              </button>
            );
          })}
          {tab === "applications" && (
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/applications.csv`}
              data-testid="admin-download-csv"
              className="ml-auto inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold transition-colors"
              rel="noopener noreferrer"
              download="studytrip-applications.csv"
            >
              Download CSV
            </a>
          )}
        </div>

        <div className="rounded-2xl bg-white border border-slate-100 overflow-hidden">
          <AdminDataTable
            tab={tab}
            items={items}
            loading={loading}
            onDelete={handleDelete}
            onStatusChange={handleStatusChange}
          />
        </div>
      </main>
    </div>
  );
}
